import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, BookOpen, BarChart3, HeadphonesIcon, Video } from 'lucide-react';

interface WelcomeMessageProps {
  plan: string;
}

const planBenefitIcons: Record<string, React.ElementType[]> = {
  Basic: [BookOpen, Check, BarChart3],
  Pro: [BookOpen, BarChart3, Video, HeadphonesIcon],
  Premium: [BookOpen, BarChart3, Video, HeadphonesIcon, Check],
};

export const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ plan }) => {
  const { t } = useTranslation();
  const benefits: string[] = t(`payments.welcome.benefits.${plan}`, { returnObjects: true, defaultValue: [] }) as string[];
  const icons = planBenefitIcons[plan] || planBenefitIcons.Pro;

  return (
    <div className="bg-gradient-to-br from-primary/5 via-white to-secondary/5 rounded-2xl p-6 md:p-8 border border-gray-100">
      <h3 className="text-xl font-bold mb-6 text-center">
        {t('payments.welcome.heading', { plan })}
      </h3>

      <ul className="space-y-4 max-w-md mx-auto">
        {Array.isArray(benefits) && benefits.map((text, index) => {
          const Icon = icons[index] || Check;
          return (
            <li 
              key={index} 
              className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              style={{
                animation: `fadeInUp 0.5s ease-out forwards`,
                animationDelay: `${index * 0.1}s`,
                opacity: 0,
              }}
            >
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-gray-700 pt-2">{text}</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 pt-6 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-600">
          {t('payments.welcome.footer')}
        </p>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomeMessage;
